import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

// React and React Native Core imports
import Page from '../../atoms/Page';
import Card from '../../atoms/Card';
import Heading from '../../atoms/Heading';
import Button from '../../atoms/Button';

// 3rd party libraries
import dayjs from 'dayjs';
import { Fzf } from 'fzf';
import _ from 'lodash';
import * as Sentry from '@sentry/react';
import { Privilege, useCan } from '@feedflo/access-control';

// Our Own imports
import ToggleAccessButton from '../../molecules/ToggleAccessButton';
import Avatar from '../../atoms/Avatar';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import TextInput from '../../atoms/TextInput';
import ConfirmationDialog from '../../organisms/ConfirmationDialog';
import { InviteButton } from '../../molecules/InviteModal';
import Combobox from '../../atoms/Combobox/Combobox';
import NotificationTierSelector from '../../molecules/NotificationTierSelector/NotificationTierSelector';
import TransportStatusBadges from '../../molecules/TransportStatusBadges';
import MessageHistoryLink from '../../molecules/MessageHistoryLink';
import useSearchParamsState from '../../utils/hooks/useSearchParamsState/useSearchParamsState';
import useUser from '../../utils/hooks/useUser';
import { getTransportStatuses } from '../../utils/transportStatus';
import { NO_NOTIFICATIONS_TIER_NAME } from '../../utils/constants';
import { resolveDefaultTierId, isTierOnlyGrant } from '../../utils/notificationTierAssignment';
import { PencilIconSVG } from '../../atoms/Icons';

// Relative path imports
import './OrgUsersTab.scss';
import OrgUsersFilterField from './OrgUsersFilterField';
import {
  ORGANIZATION_USERS_GQL,
  GET_ORG_PLAN_GQL,
  SOFT_DELETE_USER_ORGANIZATION_GQL,
  SOFT_DELETE_USER_BARN_GQL,
  UPSERT_USER_BARN_GQL,
  UPSERT_USER_ORGANIZATION_ACCESS_GQL,
  UPSERT_USER_ORGANIZATION_TIER_ASSIGNMENT_GQL,
  UPSERT_USER_BARN_TIER_ASSIGNMENT_GQL,
  DELETE_USER_ORGANIZATION_TIER_ASSIGNMENT_GQL,
  DELETE_USER_BARN_TIER_ASSIGNMENT_GQL,
} from './queries';

const asUserContext = {
  headers: {
    'x-hasura-role': 'user',
  },
};

const ORG_WIDE_ACCESS_OPTION = { id: '', name: 'Org-wide (all barns)' };

function buildAccessGrant({
  type,
  user,
  role,
  barn = null,
  tierAssignment = null,
  inheritedOrgRole = null,
  isTierOnly = false,
}) {
  const roleKeyPart = role?.id || (isTierOnly ? 'tier-only' : 'unknown');
  return {
    key: type === 'org' ? `org-${user.id}-${roleKeyPart}` : `barn-${user.id}-${barn.id}-${roleKeyPart}`,
    type,
    user,
    role,
    barn,
    inheritedOrgRole,
    isTierOnly,
    tierId: tierAssignment?.tier_id || null,
    tierAssignmentId: tierAssignment?.id || null,
  };
}

function OrgUsersTab({ titleSegments = [], organizationId = null }) {
  // --- State hooks ---
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [grantToRemove, setGrantToRemove] = useState(null);
  const [addingAccessForUserId, setAddingAccessForUserId] = useState(null);
  const [addAccessBarnId, setAddAccessBarnId] = useState('');
  const [addAccessRoleId, setAddAccessRoleId] = useState('');
  const [addAccessTierId, setAddAccessTierId] = useState('');
  const [editingBarnRoleGrantKey, setEditingBarnRoleGrantKey] = useState(null);

  // --- Atoms and other Hooks ---
  const { user: loggedInUser, loading: userLoading } = useUser();
  const { can } = useCan();

  // --- URL-driven filter state ---
  const [searchTerm, setSearchTerm] = useSearchParamsState('q', '');
  const [barnFilter, setBarnFilter] = useSearchParamsState('barn', null);
  const [sortField, setSortField] = useSearchParamsState('sort', 'name');
  const [sortDirection, setSortDirection] = useSearchParamsState('dir', 'asc');

  // --- Privileges and Access Hooks ---
  const canManageUsers =
    can('org', organizationId, Privilege.ORG_USERS_MANAGE) ||
    can('org', organizationId, Privilege.ORG_GENERAL_ADMINISTRATE);

  const refetchQueries = useMemo(
    () => [
      { query: ORGANIZATION_USERS_GQL, variables: { organization_id: organizationId }, context: asUserContext },
      { query: GET_ORG_PLAN_GQL, variables: { org_id: organizationId }, context: asUserContext },
    ],
    [organizationId],
  );

  // --- Apollo/GraphQL hooks ---
  const {
    loading,
    error,
    data,
    refetch: refetchOrgUsers,
  } = useQuery(ORGANIZATION_USERS_GQL, {
    variables: { organization_id: organizationId },
    skip: !organizationId,
    context: asUserContext,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const { data: orgPlanData } = useQuery(GET_ORG_PLAN_GQL, {
    variables: { org_id: organizationId },
    skip: !organizationId,
    fetchPolicy: 'cache-first',
    context: asUserContext,
  });

  const [softDeleteUserOrganization] = useMutation(SOFT_DELETE_USER_ORGANIZATION_GQL, {
    refetchQueries,
    context: asUserContext,
  });

  const [softDeleteUserBarn] = useMutation(SOFT_DELETE_USER_BARN_GQL, {
    refetchQueries,
    context: asUserContext,
  });

  const [upsertUserBarn] = useMutation(UPSERT_USER_BARN_GQL, {
    refetchQueries,
    context: asUserContext,
  });

  const [upsertUserOrganization] = useMutation(UPSERT_USER_ORGANIZATION_ACCESS_GQL, {
    refetchQueries,
    context: asUserContext,
  });

  const [upsertUserOrganizationTier] = useMutation(UPSERT_USER_ORGANIZATION_TIER_ASSIGNMENT_GQL, {
    refetchQueries,
    context: asUserContext,
  });

  const [deleteUserOrganizationTier] = useMutation(DELETE_USER_ORGANIZATION_TIER_ASSIGNMENT_GQL, {
    refetchQueries,
    context: asUserContext,
  });

  const [upsertUserBarnTier] = useMutation(UPSERT_USER_BARN_TIER_ASSIGNMENT_GQL, {
    refetchQueries,
    context: asUserContext,
  });

  const [deleteUserBarnTier] = useMutation(DELETE_USER_BARN_TIER_ASSIGNMENT_GQL, {
    refetchQueries,
    context: asUserContext,
  });

  // --- Derived state ---
  const pageTitleSegments = useMemo(() => ['Users', ...titleSegments], [titleSegments]);
  const orgBarns = data?.barns || [];
  const roles = data?.roles || [];
  const roleItems = useMemo(() => roles.map((role) => ({ id: role.id, name: role.name })), [roles]);
  const barnFilterItems = useMemo(
    () => orgBarns.map((barn) => ({ id: barn.id, name: barn.name })),
    [orgBarns],
  );
  const hasActiveFilters = Boolean(searchTerm || barnFilter);
  const addAccessBarnItems = useMemo(
    () => [ORG_WIDE_ACCESS_OPTION, ...orgBarns.map((barn) => ({ id: barn.id, name: barn.name }))],
    [orgBarns],
  );

  const currentSelectedPlan = orgPlanData?.organization_notification_plan?.[0];
  const planId = currentSelectedPlan?.plan_id || null;
  const planTiers = currentSelectedPlan?.plan?.tiers || [];

  const defaultTierId = useMemo(() => {
    if (!planTiers.length) return null;
    const sorted = [...planTiers].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const noNotificationsTier = sorted.find((tier) => tier.name === NO_NOTIFICATIONS_TIER_NAME);
    return noNotificationsTier?.id || sorted[0]?.id || null;
  }, [planTiers]);

  const userGroups = useMemo(() => {
    const organizationUsers = data?.organization_users || [];
    const barnUsers = data?.barn_users || [];
    const barnTierAssignments = data?.barn_tier_assignments || [];
    const userMap = new Map();

    const orgRoleMap = new Map();
    organizationUsers
      .filter((orgAccess) => !orgAccess.deleted_at)
      .forEach((orgAccess) => {
        orgRoleMap.set(orgAccess.user.id, orgAccess.role);
      });

    const activeBarnAccessKeys = new Set(
      barnUsers
        .filter((barnAccess) => !barnAccess.deleted_at && barnAccess.barn)
        .map((barnAccess) => `${barnAccess.user.id}:${barnAccess.barn.id}`),
    );

    organizationUsers
      .filter((orgAccess) => !orgAccess.deleted_at)
      .forEach((orgAccess) => {
        const user = orgAccess.user;
        if (!userMap.has(user.id)) {
          userMap.set(user.id, {
            user,
            transports: getTransportStatuses(user),
            orgTierId: orgAccess.user_organization_notification_tier_assignment?.tier_id || null,
            grants: [],
          });
        }

        const group = userMap.get(user.id);
        group.orgTierId = orgAccess.user_organization_notification_tier_assignment?.tier_id || group.orgTierId;
        group.grants.push(
          buildAccessGrant({
            type: 'org',
            user,
            role: orgAccess.role,
            tierAssignment: orgAccess.user_organization_notification_tier_assignment,
          }),
        );
      });

    barnUsers
      .filter((barnAccess) => !barnAccess.deleted_at && barnAccess.barn)
      .forEach((barnAccess) => {
        const user = barnAccess.user;
        if (!userMap.has(user.id)) {
          userMap.set(user.id, {
            user,
            transports: getTransportStatuses(user),
            orgTierId: null,
            grants: [],
          });
        }

        const group = userMap.get(user.id);
        if (!group.transports?.length) {
          group.transports = getTransportStatuses(user);
        }

        group.grants.push(
          buildAccessGrant({
            type: 'barn',
            user,
            role: barnAccess.role,
            barn: barnAccess.barn,
            tierAssignment: barnAccess.user_barn_notification_tier_assignment,
          }),
        );
      });

    barnTierAssignments.forEach((tierAssignment) => {
      const accessKey = `${tierAssignment.user_id}:${tierAssignment.barn_id}`;
      if (activeBarnAccessKeys.has(accessKey)) return;

      const user = tierAssignment.user;
      const inheritedOrgRole = orgRoleMap.get(user.id) || null;
      if (!inheritedOrgRole) return;

      if (!userMap.has(user.id)) {
        userMap.set(user.id, {
          user,
          transports: getTransportStatuses(user),
          orgTierId: null,
          grants: [],
        });
      }

      const group = userMap.get(user.id);
      if (!group.transports?.length) {
        group.transports = getTransportStatuses(user);
      }

      const orgAccess = organizationUsers.find(
        (orgUser) => !orgUser.deleted_at && orgUser.user.id === user.id,
      );
      if (orgAccess) {
        group.orgTierId =
          orgAccess.user_organization_notification_tier_assignment?.tier_id || group.orgTierId;
      }

      group.grants.push(
        buildAccessGrant({
          type: 'barn',
          user,
          role: null,
          barn: tierAssignment.barn,
          tierAssignment,
          inheritedOrgRole,
          isTierOnly: true,
        }),
      );
    });

    return Array.from(userMap.values()).map((group) => ({
      ...group,
      grants: group.grants.sort((a, b) => {
        if (a.type !== b.type) {
          return a.type === 'org' ? -1 : 1;
        }
        const barnNameA = a.type === 'org' ? 'All Barns' : a.barn?.name || '';
        const barnNameB = b.type === 'org' ? 'All Barns' : b.barn?.name || '';
        const barnCompare = barnNameA.localeCompare(barnNameB);
        if (barnCompare !== 0) return barnCompare;
        return (a.role?.name || a.inheritedOrgRole?.name || '').localeCompare(
          b.role?.name || b.inheritedOrgRole?.name || '',
        );
      }),
    }));
  }, [data]);

  const filteredAndSortedUserGroups = useMemo(() => {
    let filtered = userGroups;

    if (barnFilter) {
      filtered = filtered
        .filter((group) =>
          group.grants.some(
            (grant) => grant.type === 'org' || (grant.type === 'barn' && grant.barn?.id === barnFilter),
          ),
        )
        .map((group) => ({
          ...group,
          grants: group.grants.filter(
            (grant) => grant.type === 'org' || (grant.type === 'barn' && grant.barn?.id === barnFilter),
          ),
        }));
    }

    if (searchTerm) {
      const fzf = new Fzf(filtered, { selector: (item) => `${item.user.name} ${item.user.email}` });
      filtered = fzf.find(searchTerm)?.map((entry) => entry.item) || [];
    }

    if (sortField === 'name') {
      filtered = _.orderBy(filtered, [(group) => group.user.name, (group) => group.user.id], [sortDirection, 'asc']);
    } else if (sortField === 'barn') {
      filtered = _.orderBy(
        filtered,
        [
          (group) => group.grants[0]?.type === 'org' ? 'All Barns' : group.grants[0]?.barn?.name || '',
          (group) => group.user.name,
        ],
        [sortDirection, 'asc'],
      );
    } else if (sortField === 'role') {
      filtered = _.orderBy(
        filtered,
        [(group) => group.grants[0]?.role?.name || '', (group) => group.user.name],
        [sortDirection, 'asc'],
      );
    }

    return filtered;
  }, [userGroups, barnFilter, searchTerm, sortField, sortDirection]);

  // --- Function definitions ---
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const resolveAutoTierId = (userId) => {
    const group = userGroups.find((entry) => entry.user.id === userId);
    return group?.orgTierId || defaultTierId;
  };

  const assignTierForGrant = async ({ userId, grantType, barnId, tierId }) => {
    if (!tierId) return;

    if (grantType === 'org') {
      await upsertUserOrganizationTier({
        variables: {
          objects: [{ user_id: userId, tier_id: tierId, organization_id: organizationId }],
        },
      });
      return;
    }

    await upsertUserBarnTier({
      variables: {
        objects: [{ user_id: userId, tier_id: tierId, barn_id: barnId }],
      },
    });
  };

  const getRoleName = (roleId) => roles.find((role) => role.id === roleId)?.name;
  const getTierName = (tierId) => planTiers.find((tier) => tier.id === tierId)?.name;
  const getUserName = (userId) => userGroups.find((group) => group.user.id === userId)?.user?.name;
  const getBarnName = (barnId) => orgBarns.find((barn) => barn.id === barnId)?.name;
  const userHasOrgAccess = (userId) =>
    userGroups.find((group) => group.user.id === userId)?.grants?.some((grant) => grant.type === 'org') ?? false;

  const handleOrgTierChange = async (userId, tierId, assignmentId) => {
    const userName = getUserName(userId) || 'User';

    try {
      if (tierId === null && assignmentId) {
        await deleteUserOrganizationTier({ variables: { id: assignmentId } });
        toast.success(`Cleared ${userName}'s org-wide notification tier override`);
      } else if (tierId) {
        await upsertUserOrganizationTier({
          variables: {
            objects: [{ user_id: userId, tier_id: tierId, organization_id: organizationId }],
          },
        });
        toast.success(`Updated ${userName}'s org-wide notification tier to ${getTierName(tierId) || 'selected tier'}`);
      }
    } catch (mutationError) {
      console.error('Failed to update org notification tier:', mutationError);
      Sentry.captureException(mutationError);
      toast.warn('Error updating notification tier');
    }
  };

  const handleBarnTierChange = async (userId, barnId, tierId, assignmentId) => {
    const userName = getUserName(userId) || 'User';
    const barnName = getBarnName(barnId) || 'barn';

    try {
      if (tierId === null && assignmentId) {
        await deleteUserBarnTier({ variables: { id: assignmentId } });
        toast.success(`Cleared ${userName}'s notification tier override for ${barnName}`);
      } else if (tierId) {
        await upsertUserBarnTier({
          variables: {
            objects: [{ user_id: userId, tier_id: tierId, barn_id: barnId }],
          },
        });
        toast.success(`Updated ${userName}'s notification tier for ${barnName} to ${getTierName(tierId) || 'selected tier'}`);
      }
    } catch (mutationError) {
      console.error('Failed to update barn notification tier:', mutationError);
      Sentry.captureException(mutationError);
      toast.warn('Error updating notification tier');
    }
  };

  const handleRoleChange = async (grant, newRoleId) => {
    if (!newRoleId) return;
    if (grant.role?.id && newRoleId === grant.role.id) return;

    const newRoleName = getRoleName(newRoleId) || 'selected role';

    try {
      const now = dayjs.tz().unix();

      if (grant.type === 'org') {
        await softDeleteUserOrganization({
          variables: {
            user_id: grant.user.id,
            organization_id: organizationId,
            role_id: grant.role.id,
            now,
          },
        });
        await upsertUserOrganization({
          variables: {
            objects: [
              {
                user_id: grant.user.id,
                organization_id: organizationId,
                role_id: newRoleId,
                deleted_at: null,
              },
            ],
          },
        });
        toast.success(`Updated ${grant.user.name}'s org-wide role to ${newRoleName}`);
        return;
      }

      if (grant.isTierOnly) {
        await upsertUserBarn({
          variables: {
            objects: [
              {
                user_id: grant.user.id,
                barn_id: grant.barn.id,
                role_id: newRoleId,
                deleted_at: null,
              },
            ],
          },
        });
        setEditingBarnRoleGrantKey(null);
        toast.success(`Updated ${grant.user.name}'s role for ${grant.barn.name} to ${newRoleName}`);
        return;
      }

      await softDeleteUserBarn({
        variables: {
          user_id: grant.user.id,
          barn_id: grant.barn.id,
          role_id: grant.role.id,
          now,
        },
      });
      await upsertUserBarn({
        variables: {
          objects: [
            {
              user_id: grant.user.id,
              barn_id: grant.barn.id,
              role_id: newRoleId,
              deleted_at: null,
            },
          ],
        },
      });
      toast.success(`Updated ${grant.user.name}'s role for ${grant.barn.name} to ${newRoleName}`);
    } catch (mutationError) {
      console.error('Failed to update role:', mutationError);
      Sentry.captureException(mutationError);
      toast.warn('Error updating role');
    }
  };

  const handleRemoveGrant = (grant) => {
    setGrantToRemove(grant);
    setConfirmationDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!grantToRemove) return;

    try {
      const now = dayjs.tz().unix();

      if (grantToRemove.type === 'org') {
        await softDeleteUserOrganization({
          variables: {
            user_id: grantToRemove.user.id,
            organization_id: organizationId,
            role_id: grantToRemove.role.id,
            now,
          },
        });
        toast.success(`Removed ${grantToRemove.user.name}'s org-wide access`);
      } else if (isTierOnlyGrant(grantToRemove)) {
        if (grantToRemove.tierAssignmentId) {
          await deleteUserBarnTier({ variables: { id: grantToRemove.tierAssignmentId } });
        }
        toast.success(`Removed ${grantToRemove.user.name}'s notification tier for ${grantToRemove.barn.name}`);
      } else {
        await softDeleteUserBarn({
          variables: {
            user_id: grantToRemove.user.id,
            barn_id: grantToRemove.barn.id,
            role_id: grantToRemove.role.id,
            now,
          },
        });
        toast.success(`Removed ${grantToRemove.user.name}'s access to ${grantToRemove.barn.name}`);
      }
    } catch (mutationError) {
      console.error('Failed to remove access:', mutationError);
      Sentry.captureException(mutationError);
      toast.warn('Error occurred while removing access');
    } finally {
      setConfirmationDialogOpen(false);
      setGrantToRemove(null);
    }
  };

  const handleCancelRemove = () => {
    setConfirmationDialogOpen(false);
    setGrantToRemove(null);
  };

  const resetAddAccessForm = () => {
    setAddingAccessForUserId(null);
    setAddAccessBarnId('');
    setAddAccessRoleId('');
    setAddAccessTierId('');
  };

  const openAddAccessForm = (userId) => {
    setAddingAccessForUserId(userId);
    setAddAccessBarnId('');
    setAddAccessRoleId('');
    setAddAccessTierId(resolveAutoTierId(userId) || defaultTierId || '');
  };

  const handleAddAccess = async (
    userId,
    barnId = addAccessBarnId,
    roleId = addAccessRoleId,
    tierId = addAccessTierId,
  ) => {
    if (!tierId) return;

    const isOrgAccess = !barnId;
    const canAddTierOnly = !isOrgAccess && !roleId && userHasOrgAccess(userId);

    // Org-wide access always requires a role. Barn access requires a role unless the
    // user already has org-wide access, in which case a barn-specific tier alone is valid (Rule #1).
    if (isOrgAccess && !roleId) return;
    if (!isOrgAccess && !roleId && !canAddTierOnly) return;

    try {
      if (isOrgAccess) {
        await upsertUserOrganization({
          variables: {
            objects: [
              {
                user_id: userId,
                organization_id: organizationId,
                role_id: roleId,
                deleted_at: null,
              },
            ],
          },
        });
        await assignTierForGrant({ userId, grantType: 'org', tierId });
      } else if (roleId) {
        await upsertUserBarn({
          variables: {
            objects: [
              {
                user_id: userId,
                barn_id: barnId,
                role_id: roleId,
                deleted_at: null,
              },
            ],
          },
        });
        await assignTierForGrant({ userId, grantType: 'barn', barnId, tierId });
      } else {
        // Tier-only barn assignment: user already has org-wide access, so no barn role is needed.
        await assignTierForGrant({ userId, grantType: 'barn', barnId, tierId });
      }

      const userName = getUserName(userId) || 'User';
      const roleName = getRoleName(roleId) || 'selected role';

      if (isOrgAccess) {
        toast.success(`Granted ${userName} org-wide access as ${roleName}`);
      } else if (roleId) {
        toast.success(`Granted ${userName} access to ${getBarnName(barnId) || 'barn'} as ${roleName}`);
      } else {
        toast.success(`Set ${userName}'s notification tier for ${getBarnName(barnId) || 'barn'}`);
      }

      resetAddAccessForm();
    } catch (mutationError) {
      console.error('Failed to add access:', mutationError);
      Sentry.captureException(mutationError);
      toast.warn('Error occurred while granting access');
    }
  };

  const handleClearAllFilters = () => {
    setSearchTerm(undefined);
    setBarnFilter(undefined);
  };

  const renderRoleCell = (grant) => {
    if (!canManageUsers) {
      if (grant.isTierOnly && grant.inheritedOrgRole) {
        return <span>Org: {grant.inheritedOrgRole.name}</span>;
      }
      return <span>{grant.role?.name || '-'}</span>;
    }

    if (grant.isTierOnly && grant.inheritedOrgRole && editingBarnRoleGrantKey !== grant.key) {
      return (
        <div className="OrgUsers-InheritedRole">
          <span className="OrgUsers-InheritedRole-label">Org: {grant.inheritedOrgRole.name}</span>
          <button
            type="button"
            className="OrgUsers-InheritedRole-edit"
            onClick={() => setEditingBarnRoleGrantKey(grant.key)}
            aria-label="Set barn-specific access role"
            title="Set barn-specific access role"
          >
            <PencilIconSVG className="OrgUsers-InheritedRole-editIcon" />
          </button>
        </div>
      );
    }

    return (
      <Combobox
        className="OrgUsers-RoleCombobox"
        items={roleItems}
        value={grant.role?.id || ''}
        placeholder="Select role"
        onChange={(selectedRole) => {
          handleRoleChange(grant, selectedRole?.id);
          if (grant.isTierOnly) {
            setEditingBarnRoleGrantKey(null);
          }
        }}
      />
    );
  };

  const renderTierCell = (grant, orgTierId) => {
    const barnDefaultTierId = resolveDefaultTierId({ orgTierId, planDefaultTierId: defaultTierId });

    if (grant.type === 'org') {
      return (
        <NotificationTierSelector
          selectedTierId={grant.tierId}
          onTierChange={(tierId) => handleOrgTierChange(grant.user.id, tierId, grant.tierAssignmentId)}
          label=""
          showDescription={false}
          planId={planId}
          disabled={!canManageUsers}
          defaultTierId={defaultTierId}
          defaultLabel="(Their Default)"
        />
      );
    }

    return (
      <NotificationTierSelector
        selectedTierId={grant.tierId}
        onTierChange={(tierId) => handleBarnTierChange(grant.user.id, grant.barn.id, tierId, grant.tierAssignmentId)}
        label=""
        showDescription={false}
        planId={planId}
        disabled={!canManageUsers}
        defaultTierId={barnDefaultTierId}
        defaultLabel="(Their Default)"
      />
    );
  };

  const renderActionsCell = (grant) => {
    if (!canManageUsers) {
      return <span className="OrgUsers-NoAction">View only</span>;
    }

    if (!userLoading && grant.user.id === loggedInUser?.id) {
      return <span className="OrgUsers-NoAction">Cannot remove yourself</span>;
    }

    return (
      <ToggleAccessButton
        actionType="remove"
        onClick={() => handleRemoveGrant(grant)}
        text="Remove Access"
        title="Remove access"
      />
    );
  };

  if (error) return <span>Error {JSON.stringify(error)}</span>;

  if (loading) {
    return (
      <Page className="OrgUsersPage" titleSegments={pageTitleSegments}>
        <div className="OrgUsers-Content">
          <div className="OrgUsers-Header">
            <Heading level={3}>Organization Users</Heading>
          </div>
          <div className="OrgUsers-TableControls">
            <LoadingSkeleton className="OrgUsers-SearchSkeleton" style={{ width: '200px', height: '40px' }} />
            <LoadingSkeleton className="OrgUsers-ButtonSkeleton" style={{ width: '150px', height: '40px' }} />
          </div>
          <div className="OrgUsers-Table">
            <div className="OrgUsers-TableContainer">
              <div className="OrgUsers-TableHeader">
                <div className="OrgUsers-TableRow">
                  <div className="OrgUsers-TableCell OrgUsers-TableHeaderCell">User</div>
                  <div className="OrgUsers-TableCell OrgUsers-TableHeaderCell">Barn</div>
                  <div className="OrgUsers-TableCell OrgUsers-TableHeaderCell">Role</div>
                  <div className="OrgUsers-TableCell OrgUsers-TableHeaderCell">Notification Tier</div>
                  <div className="OrgUsers-TableCell OrgUsers-TableHeaderCell">Actions</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page className="OrgUsersPage" titleSegments={pageTitleSegments}>
      <div className="OrgUsers-Content">
        <div className="OrgUsers-Header">
          <Heading level={3}>Organization Users</Heading>
        </div>

        <div className="OrgUsers-TableControls">
          <section className="OrgUsers-FiltersPanel" aria-label="User filters">
            <div className="OrgUsers-FiltersRow">
              <OrgUsersFilterField
                className="OrgUsers-filterField--search"
                label="Search"
                hasSelection={Boolean(searchTerm)}
                onClear={() => setSearchTerm(undefined)}
                clearLabel="Clear search"
              >
                <TextInput
                  label=""
                  placeholder="Name or email"
                  value={searchTerm || ''}
                  onChange={(event) => setSearchTerm(event.target.value || undefined)}
                />
              </OrgUsersFilterField>
              <OrgUsersFilterField
                className="OrgUsers-filterField--barn"
                label="Barn"
                hasSelection={Boolean(barnFilter)}
                onClear={() => setBarnFilter(undefined)}
                clearLabel="Clear barn filter"
              >
                <Combobox
                  className="OrgUsers-BarnFilter"
                  items={barnFilterItems}
                  value={barnFilter}
                  label=""
                  placeholder="All barns"
                  onChange={(selectedBarn) => setBarnFilter(selectedBarn?.id || undefined)}
                />
              </OrgUsersFilterField>
              <button
                type="button"
                className="OrgUsers-ClearAllFilters"
                disabled={!hasActiveFilters}
                onClick={handleClearAllFilters}
                title="Clear search and barn filters"
              >
                Clear filters
              </button>
            </div>
          </section>
          <div className="OrgUsers-TableControlsSection OrgUsers-TableControlsSection--actions">
            <MessageHistoryLink
              filters={{ orgId: organizationId }}
              variant="inline"
              className="OrgUsers-OrgMessageHistoryLink"
            />
            <InviteButton
              organizationId={organizationId}
              hideCombobox={true}
              onInviteCreated={() => {
                refetchOrgUsers();
              }}
            />
          </div>
        </div>

        <div className="OrgUsers-Table">
          {filteredAndSortedUserGroups.length > 0 ? (
            <div className="OrgUsers-TableContainer">
              <div className="OrgUsers-TableHeader">
                <div className="OrgUsers-TableRow">
                  <div
                    className="OrgUsers-TableCell OrgUsers-TableHeaderCell OrgUsers-TableSortableHeader"
                    onClick={() => handleSort('name')}
                  >
                    User
                    {sortField === 'name' && (
                      <span className="OrgUsers-SortIndicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </div>
                  <div
                    className="OrgUsers-TableCell OrgUsers-TableHeaderCell OrgUsers-TableSortableHeader"
                    onClick={() => handleSort('barn')}
                  >
                    Barn
                    {sortField === 'barn' && (
                      <span className="OrgUsers-SortIndicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </div>
                  <div
                    className="OrgUsers-TableCell OrgUsers-TableHeaderCell OrgUsers-TableSortableHeader"
                    onClick={() => handleSort('role')}
                  >
                    Role
                    {sortField === 'role' && (
                      <span className="OrgUsers-SortIndicator">{sortDirection === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </div>
                  <div className="OrgUsers-TableCell OrgUsers-TableHeaderCell">Notification Tier</div>
                  <div className="OrgUsers-TableCell OrgUsers-TableHeaderCell">Actions</div>
                </div>
              </div>
              <div className="OrgUsers-TableBody">
                {filteredAndSortedUserGroups.map((group) => {
                  const { user, grants, transports, orgTierId } = group;
                  const rowSpan = grants.length + (canManageUsers ? 1 : 0);
                  const hasInvitePending = user?.invites_to?.some((invite) => !invite.accepted_at);

                  return (
                    <div key={user.id} className="OrgUsers-UserGroup">
                      <div className="OrgUsers-TableCell OrgUsers-TableCell--user" style={{ gridRow: `span ${rowSpan}` }}>
                        <div className="OrgUsers-UserInfo">
                          <Link to={`${user.id}`} className="OrgUsers-AvatarLink">
                            <Avatar src={user.avatar} alt={user.name} size="small" />
                          </Link>
                          <div className="OrgUsers-UserDetails">
                            <Link to={`${user.id}`} className="OrgUsers-UserName">
                              {user.name}
                              <span className="OrgUsers-UserName-me">
                                {!userLoading && user.id === loggedInUser?.id ? ' (me)' : ''}
                                {hasInvitePending ? ' (invite pending)' : ''}
                              </span>
                            </Link>
                            <a href={`mailto:${user.email}`} className="OrgUsers-UserEmail">
                              {user.email}
                            </a>
                            <div className="OrgUsers-UserMeta">
                              <TransportStatusBadges transports={transports} variant="compact" />
                              <MessageHistoryLink
                                filters={{ orgId: organizationId, userId: user.id }}
                                variant="compact"
                                className="OrgUsers-UserMessageHistoryLink"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {grants.map((grant) => (
                        <div key={grant.key} className="OrgUsers-TableRow">
                          <div className="OrgUsers-TableCell OrgUsers-TableCell--barn">
                            {grant.type === 'org' ? (
                              <span className="OrgUsers-BarnScope" title="Organization-wide access to all barns">
                                <span className="OrgUsers-BarnScope-text">All barns</span>
                              </span>
                            ) : (
                              <div className="OrgUsers-BarnCell">
                                <Link to={`/barns/${grant.barn.id}/settings`} className="OrgUsers-BarnLink">
                                  {grant.barn?.name || 'Unknown barn'}
                                </Link>
                              </div>
                            )}
                          </div>
                          <div className="OrgUsers-TableCell OrgUsers-TableCell--role">{renderRoleCell(grant)}</div>
                          <div className="OrgUsers-TableCell OrgUsers-TableCell--tier">{renderTierCell(grant, orgTierId)}</div>
                          <div className="OrgUsers-TableCell OrgUsers-TableCell--actions">{renderActionsCell(grant)}</div>
                        </div>
                      ))}

                      {canManageUsers && (
                        <div className="OrgUsers-TableRow OrgUsers-TableRow--addAccess">
                          {addingAccessForUserId === user.id ? (
                            <>
                              <div className="OrgUsers-TableCell OrgUsers-TableCell--barn">
                                <Combobox
                                  className="OrgUsers-AddAccessBarn"
                                  items={addAccessBarnItems}
                                  value={addAccessBarnId}
                                  placeholder="Select barn or org-wide"
                                  onChange={(selectedBarn) => setAddAccessBarnId(selectedBarn?.id || '')}
                                />
                              </div>
                              <div className="OrgUsers-TableCell OrgUsers-TableCell--role">
                                <Combobox
                                  className="OrgUsers-AddAccessRole"
                                  items={roleItems}
                                  value={addAccessRoleId}
                                  placeholder={
                                    addAccessBarnId && userHasOrgAccess(user.id)
                                      ? 'Select role (optional)'
                                      : 'Select role'
                                  }
                                  onChange={(selectedRole) => setAddAccessRoleId(selectedRole?.id || '')}
                                />
                                {addAccessBarnId && userHasOrgAccess(user.id) && (
                                  <div className="OrgUsers-AddAccessRoleHint">
                                    Optional — user already has org-wide access
                                  </div>
                                )}
                              </div>
                              <div className="OrgUsers-TableCell OrgUsers-TableCell--tier">
                                <NotificationTierSelector
                                  className="OrgUsers-AddAccessTier"
                                  selectedTierId={addAccessTierId}
                                  onTierChange={setAddAccessTierId}
                                  label=""
                                  showDescription={false}
                                  planId={planId}
                                  defaultTierId={orgTierId || defaultTierId}
                                  defaultLabel="(Their Default)"
                                />
                              </div>
                              <div className="OrgUsers-TableCell OrgUsers-TableCell--actions OrgUsers-AddAccessActions">
                                <Button
                                  content="Grant Access"
                                  disabled={
                                    !addAccessTierId ||
                                    (!addAccessRoleId && !(addAccessBarnId && userHasOrgAccess(user.id)))
                                  }
                                  onClick={() => handleAddAccess(user.id)}
                                />
                                <Button content="Cancel" variant="text" color="secondary" onClick={resetAddAccessForm} />
                              </div>
                            </>
                          ) : (
                            <div className="OrgUsers-TableCell OrgUsers-TableCell--addAccess" style={{ gridColumn: '2 / span 4' }}>
                              <Button
                                content="Add Access"
                                variant="text"
                                color="secondary"
                                onClick={() => openAddAccessForm(user.id)}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="OrgUsers-NoResults">
              {searchTerm ? `No users found matching "${searchTerm}"` : 'No users found'}
            </div>
          )}
        </div>

        {userGroups.length === 0 && (
          <div className="OrgUsers-Empty">
            <Card>
              <div className="EmptyState">
                <Heading level={4}>No users found</Heading>
                <p>This organization doesn&apos;t have any users yet.</p>
              </div>
            </Card>
          </div>
        )}

        <ConfirmationDialog
          open={confirmationDialogOpen}
          message={`Are you sure you want to remove ${grantToRemove?.user?.name}'s access?`}
          onCancel={handleCancelRemove}
          onConfirm={handleConfirmRemove}
        />
      </div>
    </Page>
  );
}

OrgUsersTab.propTypes = {
  titleSegments: PropTypes.arrayOf(PropTypes.string),
  organizationId: PropTypes.string,
};

export default OrgUsersTab;
